import React, { useState, useEffect } from 'react';
import { isLightColor } from "../../utils/LuminosityChecker";

interface SuggestedPromptsProps {
  configData: any;
  onPromptClick: (prompt: string) => void;
  messages: any[];
  allowSendMsg: boolean | string;
  themeOverride?: 'light' | 'dark' | 'auto';
}

const SuggestedPrompts: React.FC<SuggestedPromptsProps> = ({
  configData,
  onPromptClick,
  messages,
  allowSendMsg,
  themeOverride = 'auto'
}) => {
  const [animate, setAnimate] = useState(false);
  const [isMobileState, setIsMobileState] = useState(false);

  const app_ai_settings = configData && configData?.app_ai_settings;

  const [prompts, setPrompts] = useState(() => {
    const {
      ai_suggestedprompt1,
      ai_suggestedprompt2,
      ai_suggestedprompt3,
      ai_suggestedprompt4,
    } = configData.app_ai_settings;

    const promptList = [
      ai_suggestedprompt1,
      ai_suggestedprompt2,
      ai_suggestedprompt3,
      ai_suggestedprompt4,
    ].filter((p: any) => p && p.trim() !== "");

    return promptList;
  });

  // Use configData colors as default with theme override
  const backgroundColor = configData.app_ui_settings.ui_backgroundcolor;
  const configIsLight = isLightColor(backgroundColor);
  
  // Determine effective theme based on themeOverride
  const effectiveIsLight = themeOverride === 'auto' ? configIsLight : themeOverride === 'light';
  
  // ChatGPT-style input background - subtle variation of config color
  const createInputVariation = (baseColor: string, isLight: boolean) => {
    if (!baseColor || !baseColor.startsWith("#")) return baseColor;
    
    const hex = baseColor.slice(1);
    const num = parseInt(hex, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    
    // More noticeable but still subtle adjustment for input background
    const adjustment = isLight ? -8 : 8; // Darker for light themes, lighter for dark themes
    const newR = Math.max(0, Math.min(255, r + adjustment));
    const newG = Math.max(0, Math.min(255, g + adjustment));
    const newB = Math.max(0, Math.min(255, b + adjustment));
    
    return `rgb(${newR}, ${newG}, ${newB})`;
  };
    
  // Separate background for prompt buttons with slight variation
  const promptBackground = themeOverride === 'auto'
    ? createInputVariation(backgroundColor, configIsLight) // Slight variation for auto theme
    : themeOverride === 'light' 
    ? createInputVariation(backgroundColor, true) // Slight variation for light theme
    : '#2d2d30'; // Same as input for dark theme
    
  const themeTextColor = themeOverride === 'auto'
    ? configData.app_ui_settings.ui_fontcolor
    : (effectiveIsLight ? '#000000' : '#ffffff');

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobileState(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (app_ai_settings.ai_showsuggestedprompts === "true") {
      setAnimate(true);
    }
  }, []);

  useEffect(() => {
    setAnimate(false);
    const timeout = setTimeout(() => {
      setAnimate(true);
    }, 50);
    return () => clearTimeout(timeout);
  }, [messages]);

  const handlePromptClick = (prompt: string) => {
    onPromptClick(prompt);
  };

  // Don't render if conditions aren't met
  if (String(app_ai_settings.ai_showsuggestedprompts) !== "true" ||
      messages.length !== 0 ||
      String(allowSendMsg) !== "true") {
    return null;
  }

  return (
    <div
      className={`flex w-full justify-center flex-wrap max-w-6xl mx-auto px-4 ${animate ? 'fadeInAnimation' : ''}`}
      style={{
        maxWidth: '100%',
        alignItems: 'flex-start',
      }}
    >
      {prompts.map((prompt: string, index: number) => (
        <div
          key={index}
          className="group relative p-2 box-border w-full sm:w-1/2 md:w-1/4" // full width on mobile, 50% on small screens, 25% on medium+
          style={{
            height: isMobileState ? '70px' : '100px', // Much shorter on mobile
          }}
        >
          <button
            onClick={() => handlePromptClick(prompt)}
            className="rounded-[1.5rem] flex items-center justify-center px-3 py-2 w-full h-full transition-all duration-300 ease-in-out transform hover:translate-y-[-2px] hover:shadow-lg"
            style={{
              color: themeTextColor,
              background: promptBackground,
              fontSize: isMobileState ? '13px' : '14px',
              fontWeight: '400',
              textAlign: 'center',
              lineHeight: '1.3',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              height: isMobileState ? '60px' : '100px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                maxWidth: '100%',
              }}
            >
              {prompt}
            </div>
          </button>

          {/* Tooltip */}
          <div
            className={`
              absolute bottom-full mb-2
              hidden group-hover:block
              text-sm rounded-lg px-4 py-3 shadow-lg border
              whitespace-normal z-50 pointer-events-none
              ${index >= prompts.length - 2 ? 'right-0' : 'left-1/2 -translate-x-1/2'}
            `}
            style={{
              color: themeTextColor,
              background: promptBackground,
              minWidth: isMobileState ? '320px' : '250px',
              maxWidth: isMobileState ? '85vw' : '450px',
              width: 'max-content',
              borderColor: themeTextColor + '20',
              fontSize: isMobileState ? '13px' : '14px',
              lineHeight: '1.4',
              wordBreak: 'break-word',
              marginRight: index >= prompts.length - 2 ? '8px' : '0'
            }}
          >
            {prompt}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SuggestedPrompts;